/**
 * Gets the base host (without protocol, with port for dev) for the current environment.
 *
 * Example: `builder.drinkag1.com` for production and `localhost:3000` for development.
 *
 * @param inCurrentUrl The current location. Must be passed in if this function
 * is called outside of a browser context (for example from a vercel function).
 * For vercel functions, use `request.url`.
 */
export function getBaseHost(inCurrentUrl: string | undefined = undefined) {
  const inUrl = inCurrentUrl || globalThis.window?.location?.href;
  if (!inUrl) {
    throw new Error('Could not determine current location to get base host.');
  }
  const currentUrl = new URL(inUrl);

  // VERCEL_ENV is not set in dev, so use NODE_ENV in that case.
  const cEnv = process.env.VERCEL_ENV || process.env.NODE_ENV;

  if (cEnv === 'development') {
    // This should be http://localhost:3000.
    // Variable is from our defined env variables in vercel.
    const url = new URL(process.env.NEXT_PUBLIC_SITE_URL!);
    return url.host;
  }

  // Use the current url's host for all other envs.
  return currentUrl.host;
}

/**
 * Builds a url using the current environment's host and protocol and appending the given path.
 *
 * @param inCurrentUrl The current location. Must be passed in if this function
 * is called outside of a browser context (for example from a vercel function).
 * For vercel functions, use `request.url`.
 */
export function buildAppUrl(path: string = '', inCurrentUrl: string | undefined = undefined) {
  const host = getBaseHost(inCurrentUrl);

  // Dev is using http, everything else https:
  const cEnv = process.env.VERCEL_ENV || process.env.NODE_ENV;
  const protocol = cEnv === 'development' ? 'http' : 'https';

  return `${protocol}://${host}/${path}`;
}

export const stripUrlHashAndQuery = (url: string) => url.split(/[?#]/)[0];
